export const DEFAULT_NUMBER_PRECISION = 4; //same precision as in the billing app

enum EXPONENT {
	'Y' = 24, // yotta
	'Z' = 21, // zetta
	'E' = 18, // exa
	'P' = 15, // peta
	'T' = 12, // tera
	'G' = 9, // giga
	'M' = 6, // mega
	'K' = 3, // kilo
	/* eslint-disable @typescript-eslint/naming-convention */
	'm' = -3, // milli
	'μ' = -6, // micro
	'n' = -9, // nano
	'p' = -12, // pico
	'f' = -15, // femto
	'a' = -18, // atto
	'z' = -21, // zepto
	'y' = -24, // yocto
	/* eslint-enable @typescript-eslint/naming-convention */
}

export interface FormatSiNumberOptions {
	unit?: string;
	precision?: number;
	isFixedPrecision?: boolean;
}

export function formatSiNumber(value?: number | null, options: FormatSiNumberOptions = {}): string {
	const { unit = '', precision = 3, isFixedPrecision} = options;

	if (value === null || value === undefined) {
		return '';
	}

	if (value === 0 && unit === '') {
		return '0';
	}

	if (value === 0) {
		return '0' + unit;
	}

	if (value.toString().length <= precision - unit.length) {
		return (value.toString()) + unit;
	}

	const sign = value >= 0 ? '' : '-';
	let absolute = Math.abs(value);
	let exponent = 0;

	while (absolute >= 1000 && exponent < 24) {
		absolute /= 1000;
		exponent += 3;
	}

	while (absolute < 1 && exponent > -24) {
		absolute *= 1000;
		exponent -= 3;
	}

	if (exponent === 3 && absolute <= 9) {
		exponent = 0;
		absolute *= 1000;
	}

	if (exponent === 0) {
		return (value.toString()) + unit;
	}

	const e = EXPONENT[exponent];

	const suffix = `${e}${unit}`;

	const isAbsInRangeOfTensOfHundred = value >= 10000 && value < 100000;
	const isAbsInRangeOfHundredsOfThousand = value >= 100000 && value < 1000000;
	const isAbsInRangeOfMillions = value >= 1000000 && value < 100000000;
	const isAbsOverHundredMillions = value >= 100000000;

	if (isFixedPrecision) {
		absolute = parseFloat(absolute.toFixed(precision));
	} else {
		if (isAbsInRangeOfTensOfHundred || isAbsInRangeOfMillions) {
			absolute = formatAbsolute(absolute, 2);
		} else if (isAbsInRangeOfHundredsOfThousand || isAbsOverHundredMillions) {
			absolute = formatAbsolute(absolute, 1);
		} else {
			absolute = parseFloat(absolute.toPrecision(precision - suffix.length));
		}
	}

	const numberStr = absolute.toString();

	return sign + numberStr + suffix;
}

export const formatAbsolute = (value: number, digitsAfterComma: number): number => {
	const validationExp = new RegExp(`^-?[0-9]+(?:.[0-9]{0,${digitsAfterComma}})?`);
	return Number(value.toString().match(validationExp)?.[0]);
};
